/**
 * GlobalMapCommandsContext - Global Map Commands System
 *
 * This context provides a solution for components outside of MapViewContext to execute
 * commands on all active map instances without using forwardRef or prop drilling.
 *
 * PROBLEM SOLVED:
 * The TimeSeriesPlayer component is located outside of MapViewContext hierarchy,
 * but needs to call preloadAllLayers() function on all active map instances.
 * Traditional React patterns like prop drilling or forwardRef are not feasible
 * due to the component architecture.
 *
 * SOLUTION ARCHITECTURE:
 * 1. Global command broadcasting system using React Context
 * 2. Command-based communication with unique IDs and timestamps
 * 3. Each MapView instance listens for commands and executes them independently
 * 4. Automatic command cleanup to prevent re-execution
 *
 * IMPLEMENTATION DETAILS:
 * - GlobalMapCommandsProvider: Wraps the entire MapView application
 * - useGlobalMapCommands: Hook for triggering and listening to commands
 * - Command structure: { id, command, payload, timestamp }
 * - Each MapView useMap hook listens for currentCommand changes
 * - Commands are cleared after execution to avoid duplicates
 *
 * USAGE FLOW:
 * TimeSeriesPlayer → triggerPreloadAllLayers(timestampList)
 *       ↓
 * GlobalMapCommandsContext → broadcasts currentCommand
 *       ↓
 * All MapView instances → useEffect detects command change
 *       ↓
 * Execute preloadAllLayers() → clearCommand()
 *
 * BENEFITS:
 * - Decoupled architecture: TimeSeriesPlayer doesn't need MapViewContext
 * - Scalable: Works with multiple map instances (TileView, LayerView, etc.)
 * - Type-safe: Full TypeScript support
 * - Extensible: Easy to add new command types
 * - Debug-friendly: Detailed logging for each step
 *
 * INTEGRATION:
 * 1. Wrap MapView with GlobalMapCommandsProvider
 * 2. TimeSeriesPlayer uses triggerPreloadAllLayers() instead of direct context access
 * 3. Each MapView useMap hook listens for commands via useGlobalMapCommands()
 * 4. Commands are executed and automatically cleaned up
 */

import React, {
  createContext, useContext, useState, useCallback, ReactNode, useMemo,
} from 'react';
import { createLogger } from 'services/debugLogger';

const logger = createLogger('GlobalMapCommands');

/**
 * Represents a global command that can be executed across all map instances
 */
interface GlobalMapCommand {
  /** Unique identifier for the command to prevent duplicates */
  id: string;
  /** Type of command to execute */
  command: 'preloadAllLayers';
  /** Command payload - for preloadAllLayers, this is the timestamp list */
  payload: string[]; // timestampList for preloadAllLayers
  /** Timestamp when the command was created */
  timestamp: number;
}

/**
 * Context value interface for global map commands
 */
interface GlobalMapCommandsContextValue {
  /** Current active command, null when no command is pending */
  currentCommand: GlobalMapCommand | null;
  /** Trigger preload all layers command across all map instances */
  triggerPreloadAllLayers: (timestampList: string[]) => void;
  /** Clear the current command to prevent re-execution */
  clearCommand: () => void;
}

const GlobalMapCommandsContext = createContext<GlobalMapCommandsContextValue>({
  currentCommand: null,
  triggerPreloadAllLayers: () => {},
  clearCommand: () => {},
});

/**
 * Hook to access global map commands functionality
 * @returns GlobalMapCommandsContextValue with command state and trigger functions
 */
export const useGlobalMapCommands = () => useContext(GlobalMapCommandsContext);

interface GlobalMapCommandsProviderProps {
  children: ReactNode;
}

/**
 * Provider component for global map commands system
 * Manages command state and provides functions to trigger and clear commands
 *
 * @param children - React components that will have access to the global commands
 */
export function GlobalMapCommandsProvider({ children }: GlobalMapCommandsProviderProps) {
  const [currentCommand, setCurrentCommand] = useState<GlobalMapCommand | null>(null);

  /**
   * Triggers preloadAllLayers command across all map instances
   * Creates a unique command with ID and timestamp for tracking
   *
   * @param timestampList - Array of timestamp strings for preloading
   */
  const triggerPreloadAllLayers = useCallback((timestampList: string[]) => {
    const command: GlobalMapCommand = {
      id: `preload_${Date.now()}_${Math.random()}`,
      command: 'preloadAllLayers',
      payload: timestampList,
      timestamp: Date.now(),
    };

    logger.info('Triggering preloadAllLayers command', command);
    setCurrentCommand(command);
  }, []);

  /**
   * Clears the current command to prevent re-execution
   * Called automatically after command execution in map instances
   */
  const clearCommand = useCallback(() => {
    setCurrentCommand(null);
  }, []);

  const value = useMemo<GlobalMapCommandsContextValue>(() => ({
    currentCommand,
    triggerPreloadAllLayers,
    clearCommand,
  }), [currentCommand, triggerPreloadAllLayers, clearCommand]);

  return (
    <GlobalMapCommandsContext.Provider value={value}>
      {children}
    </GlobalMapCommandsContext.Provider>
  );
}

/*
 * IMPLEMENTATION NOTES:
 *
 * 1. FILES MODIFIED:
 *    - /src/contexts/GlobalMapCommandsContext/index.tsx (this file - new)
 *    - /src/contexts/MapViewContext/hooks/useMap.tsx (added command listener)
 *    - /src/pages/MapView/index.tsx (wrapped with GlobalMapCommandsProvider)
 *    - /src/pages/MapView/components/.../TimeSeriesPlayer/index.tsx (uses triggerPreloadAllLayers)
 *
 * 2. COMMAND EXECUTION FLOW:
 *    TimeSeriesPlayer calls triggerPreloadAllLayers() when date range changes
 *    → GlobalMapCommandsContext broadcasts command to all map instances
 *    → Each useMap hook detects currentCommand change via useEffect
 *    → preloadAllLayers() executed independently on each map
 *    → clearCommand() called to prevent re-execution
 *
 * 3. EXTENSIBILITY:
 *    To add new command types, extend the GlobalMapCommand interface:
 *    command: 'preloadAllLayers' | 'newCommandType'
 *    Add corresponding trigger functions and handlers in useMap
 *
 * 4. DEBUGGING:
 *    All commands are logged with 🌍 prefix for easy identification
 *    Each map execution is logged with 🗺️ prefix and map index
 *    Check browser console for command flow debugging
 */
